import React, { useRef } from 'react';
import { FileText, Eye, BarChart, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

const TestResultsViewer = ({ tests, onSelectTest, onViewPdf, onDeleteTest }) => {
  const sliderRef = useRef(null);
  
  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = direction === 'left' ? -340 : 340; // Width of card + gap
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Group tests by year for better organization
  const groupedTests = tests.reduce((acc, test) => {
    // Extract year from date string - handle different date formats
    let year;
    if (test.date) {
      // Try different date format patterns
      // eslint-disable-next-line
      const dateMatch = test.date.match(/\d{4}/) || // Find 4-digit year anywhere
                       // eslint-disable-next-line
                       test.date.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/) || // DD/MM/YYYY or DD-MM-YYYY
                       // eslint-disable-next-line
                       test.date.match(/(\d{4})[/-](\d{1,2})[/-](\d{1,2})/); // YYYY/MM/DD or YYYY-MM-DD
      
      year = dateMatch ? (dateMatch[3] || dateMatch[1] || dateMatch[0]) : new Date().getFullYear().toString();
    } else {
      year = new Date().getFullYear().toString();
    }
    
    if (!acc[year]) acc[year] = [];
    acc[year].push(test);
    return acc;
  }, {});

  // Sort years in reverse order (newest first)
  const sortedYears = Object.keys(groupedTests).sort((a, b) => b - a);
  
  return (
    <div className="space-y-8">
      {sortedYears.map(year => (
        <div key={year} className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-700">{year}</h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => scroll('left')}
                className="p-2 rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-red-500"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button 
                onClick={() => scroll('right')}
                className="p-2 rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-red-500"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div 
            ref={sliderRef}
            className="flex overflow-x-auto scrollbar-hide gap-6 pb-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {groupedTests[year].slice().reverse().map((test, index) => (
              <div 
                key={index} 
                className="flex-shrink-0 w-80 snap-start bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <FileText className="w-4 h-4 text-red-500" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 truncate">{test.name}</h3>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                    <span className="text-xs text-gray-500">{test.date}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => onViewPdf(test.url)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-xs transition-all duration-200"
                    >
                      <Eye className="w-3 h-3" />
                      <span>View</span>
                    </button>
                    
                    <button 
                      onClick={() => onSelectTest(test)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-red-50 border border-red-100 rounded-lg text-red-600 hover:bg-red-100 text-xs transition-all duration-200"
                    >
                      <BarChart className="w-3 h-3" />
                      <span>Analyze</span>
                    </button>
                    
                    <button 
                      onClick={() => onDeleteTest(test)}
                      className="flex items-center justify-center p-2 bg-white border border-gray-200 rounded-lg text-gray-500 hover:text-red-500 hover:bg-gray-50 text-xs transition-all duration-200"
                      title="Delete test"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      
      {/* Empty state */}
      {tests.length === 0 && (
        <div className="text-center py-8">
          <div className="inline-flex p-3 rounded-full bg-red-50 mb-3">
            <FileText className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-gray-600 font-medium">No blood test files found</p>
          <p className="text-sm text-gray-500 mt-2">Upload blood test files to analyze them</p>
        </div>
      )}

      {/* Add this to your CSS */}
      <style jsx="true">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default TestResultsViewer;
